import { FaWhatsapp } from 'react-icons/fa'; // Importando o ícone do WhatsApp
import DeleteIcon from '@/components/_common/DeleteIcon';
import EditIcon from '@/components/_common/EditIcon';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import { useSuppliersReduce } from '@/core/domain/people/Suppliers/Suppliers.hooks';
import {
  ListSuppliersQuery,
  SupplierReduced,
} from '@/core/domain/people/Suppliers/Suppliers.types';
import { useWindowSize } from '@/hooks';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { applyMask, removeMask } from '@/utils/mask';
import { cellphoneMask, cnpjMask, cpfMask } from '@/utils/mask/commonMasks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Divider, Space } from 'antd';
import { useIntl } from 'react-intl';

interface Props {
  handleRemoveClick: (record: SupplierReduced) => void;
  queryParams?: ListSuppliersQuery;
}

const SuppliersList: React.FC<Props> = ({ handleRemoveClick, queryParams }) => {
  const intl = useIntl();
  const { isMobile } = useWindowSize();
  const { data, isLoading, fetchNextPage, hasNextPage } =
    useSuppliersReduce(queryParams);

  const suppliers = flatInfiniteData(data);

  const redirectToWhatsApp = (number: string) => {
    const urlQueryString = new URLSearchParams({
      phone: `55${number}`,
    }).toString();
    const baseUrl = isMobile
      ? `https://wa.me/55${number}`
      : `https://web.whatsapp.com/send?${urlQueryString}`;

    window.open(baseUrl, '_blank');
  };

  const formatMask = (phone: string) =>
    applyMask(removeMask(phone), cellphoneMask);

  const columns: ColumnsType<SupplierReduced> = [
    {
      dataIndex: 'name',
      title: intl.formatMessage({ id: 'suppliers.list.header.name' }),
      render: (value) => value || '-',
      ellipsis: true,
      width: 200,
    },
    {
      dataIndex: 'cpfCnpj',
      title: intl.formatMessage({ id: 'suppliers.list.header.cpfCnpj' }),
      render: (_, record) =>
        (record.cnpj && applyMask(record.cnpj, cnpjMask)) ||
        (record.cpf && applyMask(record.cpf, cpfMask)),
      ellipsis: true,
      width: 230,
    },
    {
      dataIndex: 'email',
      title: intl.formatMessage({ id: 'suppliers.list.header.email' }),
      render: (value) => value || '-',
      ellipsis: true,
      width: 200,
    },
    {
      dataIndex: 'phone',
      title: intl.formatMessage({ id: 'suppliers.list.header.contact' }),
      render: (_, row) =>
        row.phone ? (
          row.isPhoneWhatsAppContact ? (
            <Space>
              <a
                onClick={() =>
                  row.phone ? redirectToWhatsApp(row.phone) : undefined
                }
                style={{
                  textDecoration: 'underline',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <FaWhatsapp size={15} />
                {formatMask(row.phone)}
              </a>
            </Space>
          ) : (
            formatMask(row.phone)
          )
        ) : (
          '-'
        ),
      ellipsis: true,
      width: 200,
    },
    {
      title: intl.formatMessage({ id: 'suppliers.list.header.actions' }),
      ellipsis: true,
      width: 100,
      align: 'right',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <EditIcon
            redirect={`/app/registry/suppliers/${record.id}/general`}
            disabled={!hasPermissions([Authorities.REGISTER_SUPPLIER_VIEW])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(record)}
            disabled={!hasPermissions([Authorities.REGISTER_SUPPLIER_DELETE])}
          />
        </Space>
      ),
    },
  ];

  return (
    <InfiniteTable
      columns={columns}
      dataSource={suppliers}
      loading={isLoading}
      rowKey={(row) => row.id}
      pagination={false}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
    />
  );
};

export default SuppliersList;
